package com.evoapps.evoshift.domain.requests

import com.evoapps.evoshift.domain.market.payroll.PayrollRules
import com.evoapps.evoshift.domain.model.Absence
import com.evoapps.evoshift.domain.model.AbsenceStatus
import com.evoapps.evoshift.domain.model.AbsenceType
import com.evoapps.evoshift.domain.model.CareRecipient
import com.evoapps.evoshift.domain.model.LimitUnit
import com.evoapps.evoshift.domain.model.RequestSettings
import com.evoapps.evoshift.domain.model.WorkRequest
import com.evoapps.evoshift.domain.model.WorkRequestKind
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalTime
import kotlinx.datetime.plus
import kotlin.math.max
import kotlin.math.round

/**
 * SH-223: one yearly limit of a person: [used] by approved requests, [pending] by those waiting.
 * [enforced]: a request over it is refused (a manager may still approve one with a note);
 * otherwise only a hint (the ZUS care-allowance days, checked by ZUS).
 */
data class RequestCounter(
    val type: AbsenceType,
    val unit: LimitUnit,
    val limit: Double,
    val used: Double,
    val pending: Double,
    /** The unit was set by an earlier request this year (art. 188 § 2, art. 148¹ § 2 KP). */
    val unitFixed: Boolean = true,
    val enforced: Boolean = true,
    /** The care allowance: whom the counter is for (null: every recipient). */
    val careFor: CareRecipient? = null
) {
    /** What is left with the waiting requests counted. */
    val remaining: Double get() = max(0.0, limit - used - pending)
    /** What is left counting the approved ones only (what a manager approves against). */
    val remainingApproved: Double get() = max(0.0, limit - used)
}

/** SH-223: whether a request fits its type's limits. */
sealed interface LimitCheck {
    data object Ok : LimitCheck
    /** More than what is left: [requested] against [counter] (in its unit). */
    data class Exceeds(val counter: RequestCounter, val requested: Double) : LimitCheck
    /** This year's unit is [unit], set by the first request of the year; the request is in the other one. */
    data class UnitMismatch(val unit: LimitUnit) : LimitCheck
    /** Longer than its type allows at once ([maxDays]: blood donation, urlop okolicznościowy). */
    data class TooLong(val maxDays: Int) : LimitCheck
    /** Something the type needs is missing (the occasion of urlop okolicznościowy). */
    data object Incomplete : LimitCheck
}

/**
 * SH-223: the yearly limits and counters of the request types where the law sets a number, pure.
 * Counters are never stored: they are worked out from the requests themselves each time (so two
 * devices can never leave a stale count), and checked again against a fresh read when a manager
 * approves. Days are working days (weekends and holidays do not count, [PayrollRules.isLeaveDay])
 * except for the care allowance, which ZUS counts in calendar days. A whole day counted in hours
 * is 8 hours.
 *
 * Simplified on purpose: part-time limits are not pro-rated (art. 148¹ § 2, art. 188 § 3 KP:
 * proportionally to the FTE); the manager's override note covers them.
 */
object RequestCounters {
    const val DAY_HOURS = 8.0
    /** Time off in lieu granted unasked: 1.5 hours off for each overtime hour (art. 151² § 2 KP). */
    const val EMPLOYER_TIME_OFF_RATIO = 1.5

    /** "HH:mm" as a time, null when it is not one ("24:00" reads as midnight). */
    fun time(text: String): LocalTime? {
        val parts = text.trim().split(":")
        val h = parts.getOrNull(0)?.toIntOrNull() ?: return null
        val m = parts.getOrNull(1)?.toIntOrNull() ?: 0
        if (h == 24 && m == 0) return LocalTime(0, 0)
        if (h !in 0..23 || m !in 0..59) return null
        return LocalTime(h, m)
    }

    /** The hours from [start] to [end] ("HH:mm"), over midnight when [end] is earlier; 0 when unreadable. */
    fun hoursOf(start: String?, end: String?): Double {
        val from = start?.let { time(it) } ?: return 0.0
        val to = end?.let { time(it) } ?: return 0.0
        val minutes = (to.hour * 60 + to.minute) - (from.hour * 60 + from.minute)
        val total = if (minutes <= 0) minutes + 24 * 60 else minutes
        return total / 60.0
    }

    private fun parse(date: String): LocalDate? = runCatching { LocalDate.parse(date.take(10)) }.getOrNull()

    private fun days(start: LocalDate, end: LocalDate, year: Int?): Sequence<LocalDate> {
        val from = if (year != null) maxOf(start, LocalDate(year, 1, 1)) else start
        val to = if (year != null) minOf(end, LocalDate(year, 12, 31)) else end
        if (to < from) return emptySequence()
        return generateSequence(from) { it.plus(DatePeriod(days = 1)) }.takeWhile { it <= to }
    }

    /** Calendar days of [absence] (in [year], null: all). */
    fun calendarDays(absence: Absence, year: Int? = null): Int {
        val start = parse(absence.startDate) ?: return 0
        val end = parse(absence.endDate) ?: start
        return days(start, end, year).count()
    }

    /** Working days of [absence] by [rules] (in [year], null: all). */
    fun workingDays(absence: Absence, rules: PayrollRules, year: Int? = null): Int {
        val start = parse(absence.startDate) ?: return 0
        val end = parse(absence.endDate) ?: start
        return days(start, end, year).count { rules.isLeaveDay(it) }
    }

    /**
     * How much of a limit [absence] takes in [unit]: its hours (part of a day), or its working
     * days (whole days: 8 hours each when counted in hours). Calendar days for the care allowance.
     */
    fun amount(absence: Absence, unit: LimitUnit, rules: PayrollRules, year: Int? = null): Double {
        if (absence.type == AbsenceType.CARE_ALLOWANCE) return calendarDays(absence, year).toDouble()
        return when {
            absence.isHourly -> if (unit == LimitUnit.HOURS) hoursOf(absence.startTime, absence.endTime) else 1.0
            unit == LimitUnit.HOURS -> workingDays(absence, rules, year) * DAY_HOURS
            else -> workingDays(absence, rules, year).toDouble()
        }
    }

    /** The unit [absence] is in by itself: hours when it covers part of a day. */
    fun ownUnit(absence: Absence): LimitUnit = absence.limitUnit ?: if (absence.isHourly) LimitUnit.HOURS else LimitUnit.DAYS

    private fun inYear(absence: Absence, year: Int): Boolean {
        val start = parse(absence.startDate) ?: return false
        val end = parse(absence.endDate) ?: start
        return start.year <= year && end.year >= year
    }

    private fun counts(absence: Absence, type: AbsenceType, userId: String, workplaceId: String?, year: Int, excludeId: String?) =
        absence.type == type && absence.userId == userId && absence.status != AbsenceStatus.REJECTED &&
            (workplaceId == null || absence.workplaceId == null || absence.workplaceId == workplaceId) &&
            absence.id != excludeId && inYear(absence, year)

    /**
     * The unit of [type] for [year]: that of the year's first request (by when it was sent; not a
     * rejected one), null when there is none yet.
     */
    fun yearUnit(type: AbsenceType, userId: String, workplaceId: String?, year: Int, absences: List<Absence>, excludeId: String? = null): LimitUnit? =
        absences.filter { counts(it, type, userId, workplaceId, year, excludeId) }
            .minWithOrNull(compareBy<Absence>({ it.metadata.createdAt }, { it.startDate }))
            ?.let { ownUnit(it) }

    /**
     * The counter of [type] for [userId] in [year], or null for a type without a yearly limit.
     * [unitIfNew]: the unit to count in when no request this year set it yet (the one being made).
     * [excludeId]: a request left out (the one being decided).
     */
    fun counter(
        type: AbsenceType, userId: String, workplaceId: String?, year: Int, absences: List<Absence>,
        settings: RequestSettings, rules: PayrollRules, unitIfNew: LimitUnit? = null, excludeId: String? = null,
        careFor: CareRecipient? = null
    ): RequestCounter? {
        val s = settings.clamped()
        val theirs = absences.filter { counts(it, type, userId, workplaceId, year, excludeId) }
        fun build(unit: LimitUnit, limit: Double, fixed: Boolean = true, enforced: Boolean = true, of: List<Absence> = theirs, recipient: CareRecipient? = null) = RequestCounter(
            type = type, unit = unit, limit = limit,
            used = round2(of.filter { it.status == AbsenceStatus.APPROVED }.sumOf { amount(it, unit, rules, year) }),
            pending = round2(of.filter { it.status == AbsenceStatus.PENDING }.sumOf { amount(it, unit, rules, year) }),
            unitFixed = fixed, enforced = enforced, careFor = recipient
        )
        return when (type) {
            AbsenceType.CHILDCARE, AbsenceType.FORCE_MAJEURE -> {
                val chosen = yearUnit(type, userId, workplaceId, year, absences, excludeId)
                val unit = chosen ?: unitIfNew ?: LimitUnit.DAYS
                val (days, hours) = if (type == AbsenceType.CHILDCARE) s.childcareDays to s.childcareHours else s.forceMajeureDays to s.forceMajeureHours
                build(unit, if (unit == LimitUnit.HOURS) hours.toDouble() else days.toDouble(), fixed = chosen != null)
            }
            AbsenceType.CARER_LEAVE -> build(LimitUnit.DAYS, s.carerLeaveDays.toDouble())
            AbsenceType.CARE_ALLOWANCE -> when (careFor) {
                CareRecipient.CHILD -> build(LimitUnit.DAYS, s.careAllowanceChildDays.toDouble(), enforced = false,
                    of = theirs.filter { (it.careFor ?: CareRecipient.CHILD) == CareRecipient.CHILD }, recipient = CareRecipient.CHILD)
                CareRecipient.FAMILY -> build(LimitUnit.DAYS, s.careAllowanceFamilyDays.toDouble(), enforced = false,
                    of = theirs.filter { it.careFor == CareRecipient.FAMILY }, recipient = CareRecipient.FAMILY)
                null -> build(LimitUnit.DAYS, s.careAllowanceTotalDays.toDouble(), enforced = false)
            }
            else -> null
        }
    }

    /** SH-223: the care allowance's three counters (children, other family, in all), as hints. */
    fun careAllowance(userId: String, workplaceId: String?, year: Int, absences: List<Absence>, settings: RequestSettings, rules: PayrollRules): List<RequestCounter> =
        listOf(CareRecipient.CHILD, CareRecipient.FAMILY, null).mapNotNull {
            counter(AbsenceType.CARE_ALLOWANCE, userId, workplaceId, year, absences, settings, rules, careFor = it)
        }

    /** The overtime hours of [requests] approved for [userId] at [workplaceId]. */
    fun approvedOvertimeHours(userId: String, workplaceId: String, requests: List<WorkRequest>): Double =
        requests.filter { it.userId == userId && it.workplaceId == workplaceId && it.kind == WorkRequestKind.OVERTIME && it.status == AbsenceStatus.APPROVED }
            .sumOf { request ->
                val days = parse(request.startDate)?.let { start -> days(start, parse(request.endDate) ?: start, null).count() } ?: 1
                hoursOf(request.startTime, request.endTime) * days
            }

    /**
     * The overtime hours [absence] (time off in lieu) gives back: as stored, else its own hours
     * (1:1, the employee asked), or two thirds of them when [grantedByEmployer] (1.5:1).
     */
    fun compensatedHours(absence: Absence, rules: PayrollRules, grantedByEmployer: Boolean = false): Double {
        absence.compensatedOvertimeHours?.let { return it }
        val hours = amount(absence, LimitUnit.HOURS, rules)
        return round2(if (grantedByEmployer) hours / EMPLOYER_TIME_OFF_RATIO else hours)
    }

    /**
     * SH-223: the overtime that can still be taken as time off: [limit] the approved overtime
     * hours, [RequestCounter.used] and [RequestCounter.pending] what approved and waiting time off
     * in lieu gives back (all years: time off may follow overtime of an earlier period).
     */
    fun overtimeBalance(
        userId: String, workplaceId: String, overtime: List<WorkRequest>, absences: List<Absence>, rules: PayrollRules, excludeId: String? = null
    ): RequestCounter {
        val timeOff = absences.filter {
            it.type == AbsenceType.TIME_OFF_IN_LIEU && it.userId == userId && it.status != AbsenceStatus.REJECTED &&
                (it.workplaceId == null || it.workplaceId == workplaceId) && it.id != excludeId
        }
        return RequestCounter(
            type = AbsenceType.TIME_OFF_IN_LIEU,
            unit = LimitUnit.HOURS,
            limit = round2(approvedOvertimeHours(userId, workplaceId, overtime)),
            used = round2(timeOff.filter { it.status == AbsenceStatus.APPROVED }.sumOf { compensatedHours(it, rules) }),
            pending = round2(timeOff.filter { it.status == AbsenceStatus.PENDING }.sumOf { compensatedHours(it, rules) })
        )
    }

    /**
     * Whether [request] fits: against everything not rejected ([againstApproved] false: what an
     * employee sends) or the approved requests only (true: what a manager approves).
     */
    fun check(
        request: Absence, absences: List<Absence>, settings: RequestSettings, rules: PayrollRules,
        overtime: List<WorkRequest> = emptyList(), againstApproved: Boolean = false
    ): LimitCheck {
        val s = settings.clamped()
        val year = parse(request.startDate)?.year ?: return LimitCheck.Ok
        val workplaceId = request.workplaceId
        // The request itself never counts against its own limit.
        val others = absences.filter { request.id.isEmpty() || it.id != request.id }
        fun left(counter: RequestCounter) = if (againstApproved) counter.remainingApproved else counter.remaining
        return when (request.type) {
            AbsenceType.BLOOD_DONATION ->
                if (calendarDays(request) > s.bloodDonationDays) LimitCheck.TooLong(s.bloodDonationDays) else LimitCheck.Ok
            AbsenceType.OCCASIONAL_LEAVE -> {
                val occasion = request.occasion ?: return LimitCheck.Incomplete
                if (workingDays(request, rules) > occasion.days) LimitCheck.TooLong(occasion.days) else LimitCheck.Ok
            }
            AbsenceType.CHILDCARE, AbsenceType.FORCE_MAJEURE -> {
                val chosen = yearUnit(request.type, request.userId, workplaceId, year, others)
                val own = ownUnit(request)
                if (chosen == LimitUnit.DAYS && request.isHourly) return LimitCheck.UnitMismatch(LimitUnit.DAYS)
                val counter = counter(request.type, request.userId, workplaceId, year, others, s, rules, unitIfNew = own) ?: return LimitCheck.Ok
                val requested = amount(request, counter.unit, rules, year)
                if (requested > left(counter) + 1e-9) LimitCheck.Exceeds(counter, requested) else LimitCheck.Ok
            }
            AbsenceType.CARER_LEAVE -> {
                val counter = counter(request.type, request.userId, workplaceId, year, others, s, rules) ?: return LimitCheck.Ok
                val requested = amount(request, LimitUnit.DAYS, rules, year)
                if (requested > left(counter) + 1e-9) LimitCheck.Exceeds(counter, requested) else LimitCheck.Ok
            }
            AbsenceType.TIME_OFF_IN_LIEU -> {
                val wp = workplaceId ?: return LimitCheck.Ok
                val balance = overtimeBalance(request.userId, wp, overtime, others, rules)
                val requested = compensatedHours(request, rules)
                if (requested > left(balance) + 1e-9) LimitCheck.Exceeds(balance, requested) else LimitCheck.Ok
            }
            else -> LimitCheck.Ok
        }
    }

    private fun round2(value: Double) = round(value * 100) / 100
}

/** SH-223: a statutory length shown as a hint (never a hard limit). */
sealed interface StatutoryHint {
    /** Up to [weeks] weeks (maternity by the children born at once; parental 41 or 43). */
    data class Weeks(val weeks: Int) : StatutoryHint
    /** Paternity: 2 weeks, until the child is 12 months old (in at most two parts of a week or more). */
    data class Paternity(val weeks: Int = 2, val withinMonths: Int = 12) : StatutoryHint
    /** Urlop wychowawczy: up to 36 months, until the end of the year the child turns 6. */
    data class ChildcareLeave(val months: Int = 36, val untilAge: Int = 6) : StatutoryHint
    /** A fixed number of days ([days]) per occasion or donation. */
    data class Days(val days: Int) : StatutoryHint

    companion object {
        /** Urlop macierzyński by the number of children born at once (art. 180 § 1 KP). */
        fun maternityWeeks(children: Int): Int = when {
            children <= 1 -> 20
            children == 2 -> 31
            children == 3 -> 33
            children == 4 -> 35
            else -> 37
        }

        /** Urlop rodzicielski (art. 182¹a § 1 KP, since 26 April 2023): 41 weeks, 43 for more children at once. */
        fun parentalWeeks(children: Int): Int = if (children <= 1) 41 else 43

        fun of(type: AbsenceType, children: Int = 1, settings: RequestSettings = RequestSettings()): StatutoryHint? = when (type) {
            AbsenceType.MATERNITY -> Weeks(maternityWeeks(children))
            AbsenceType.PARENTAL -> Weeks(parentalWeeks(children))
            AbsenceType.PATERNITY -> Paternity()
            AbsenceType.CHILDCARE_LEAVE -> ChildcareLeave()
            AbsenceType.BLOOD_DONATION -> Days(settings.clamped().bloodDonationDays)
            else -> null
        }
    }
}
